<template>
  <!-- eslint-disable -->
  <div class="event-feed-container">
    <div v-if="events.length === 0" class="empty-state">
      <i class="el-icon-document"></i>
      <p>No events found</p>
    </div>

    <div v-for="event in events" :key="event.id" class="event-item" v-infinite-scroll="load" style="overflow:auto">
      <component :is="getEventCardComponent(event)" :event="event" @view-details="$emit('view-details', $event)" @view-resource="$emit('view-resource', $event)" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'EventNewsfeed',
  components: {
    FailedLoginCard: () => import('./Cards/FailedLogin.vue'),
    TrackingTimeslotCard: () => import('./Cards/TrackingTimeslot.vue'),
    AccountCard: () => import('./Cards/Account.vue'),
    PasswordSetCard: () => import('./Cards/PasswordSet.vue'),
    TrackingErrorCard: () => import('./Cards/TrackingError.vue')
  },
  props: {
    events: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  methods: {
    load () {
      this.$emit('load-events')
    },
    getEventCardComponent (event) {
      switch (event.event_type) {
        case 'failed_login':
          return 'FailedLoginCard'
        case 'tracking.timeslot_scheduled':
          return 'TrackingTimeslotCard'
        case 'account.updated':
          return 'AccountCard'
        case 'profile.password_set':
          return 'PasswordSetCard'
        case 'tracking.error':
        case 'tracking.undelivered':
        case 'tracking.attempted_pickup':
        case 'tracking:error':
        case 'tracking:undelivered':
        case 'tracking:attempted_pickup':
          return 'TrackingErrorCard'
        // Add more specific event type components here as needed
        default:
          return 'DefaultEventCard'
      }
    },
    getEventIcon (eventType) {
      const type = eventType.split('.')[0]
      const icons = {
        shipment: 'el-icon-box',
        account: 'el-icon-user',
        profile: 'el-icon-user-solid',
        document: 'el-icon-document',
        invoice: 'el-icon-tickets',
        payment: 'el-icon-money'
      }
      return icons[type] || 'el-icon-info'
    },

    getEventColor (eventType) {
      const action = eventType.split('.')[1]
      const colors = {
        created: '#67C23A',
        updated: '#409EFF',
        deleted: '#F56C6C',
        delivered: '#67C23A',
        in_transit: '#E6A23C',
        delayed: '#F56C6C',
        suspended: '#F56C6C',
        uploaded: '#409EFF',
        approved: '#67C23A',
        rejected: '#F56C6C',
        cancelled: '#909399'
      }
      return colors[action] || '#909399'
    },

    getStatusType (status) {
      const types = {
        active: 'success',
        pending: 'warning',
        in_transit: 'primary',
        delivered: 'success',
        delayed: 'danger',
        suspended: 'danger',
        pending_review: 'warning',
        cancelled: 'info',
        approved: 'success',
        rejected: 'danger',
        completed: 'success'
      }
      return types[status] || ''
    },

    formatEventType (eventType) {
      return eventType
        .split('.')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).replace(/_/g, ' '))
        .join(' - ')
    },

    formatDate (dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)

      if (minutes < 1) return 'Just now'
      if (minutes < 60) return `${minutes}m ago`
      if (hours < 24) return `${hours}h ago`
      if (days < 7) return `${days}d ago`

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      })
    },

    formatKey (key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    },

    formatValue (value) {
      if (Array.isArray(value)) {
        return value.join(', ')
      }
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value)
      }
      return value
    }
  }
}
</script>

<style>
.event-feed-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.event-item {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: all 0.3s;
}

.event-item:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  transform: translateY(-2px);
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.event-type {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.event-type i {
  font-size: 20px;
}

.event-type-text {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
}

.event-time {
  font-size: 13px;
  color: #909399;
  white-space: nowrap;
}

.event-resource {
  margin: 12px 0;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 6px;
  border-left: 3px solid #409eff;
}

.resource-name {
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.resource-id {
  font-size: 13px;
  color: #606266;
  font-family: 'Monaco', 'Courier New', monospace;
}

.event-data {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

.data-item {
  display: flex;
  gap: 8px;
  margin: 6px 0;
  font-size: 14px;
  color: #606266;
}

.data-label {
  font-weight: 500;
  color: #909399;
  min-width: 120px;
}

.event-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
  background: white;
  border-radius: 8px;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}
</style>
